import {
  PriceBookStatus,
  PricebookPeriodStatus,
} from "../../../types/price-book.type";
import { ProductStatus } from "../../../types/product.type";
import { formatNumber } from "../../../utils";

/** Products and pricebooks carry the same enable/disable set, so one map serves both. */
export type CatalogueStatus = ProductStatus | PriceBookStatus;

export const STATUS_LABEL: Record<CatalogueStatus, string> = {
  enable: "ใช้งาน",
  disable: "ปิด",
};

/** A catalogue price as "18.00 บาท", or "-" for a price that was never set. */
export const formatCataloguePrice = (price: number | null): string =>
  price === null ? "-" : `${formatNumber(price)} บาท`;

/** Sentinel a filter select uses for "no filter"; stripped before it reaches the API. */
export const ALL_FILTER_VALUE = "all";

export const STATUS_FILTER_OPTIONS: {
  value: CatalogueStatus | typeof ALL_FILTER_VALUE;
  label: string;
}[] = [
  { value: ALL_FILTER_VALUE, label: "ทั้งหมด" },
  { value: "enable", label: STATUS_LABEL.enable },
  { value: "disable", label: STATUS_LABEL.disable },
];

/** Drops the "all"/empty sentinel filter values before they reach the API. */
export const cleanParam = (value: string) =>
  value && value !== ALL_FILTER_VALUE ? value : undefined;

/**
 * Same job as `cleanParam` for the status select, but narrowed to the API enum so
 * the caller never has to assert the widened `string` the select hands back.
 */
export const cleanStatusParam = (value: string): CatalogueStatus | undefined =>
  value === "enable" || value === "disable" ? value : undefined;

/** Zebra stripe behind every odd catalogue table row. */
export const STRIPE_BACKGROUND = "#f6f6f9";

/** Vertical and horizontal padding every catalogue table body cell carries, in px. */
export const CATALOGUE_CELL_PADDING = 6;

/** Rendered height of a MUI `size="small"` IconButton — a 20px icon inside 5px padding. */
export const SMALL_ICON_BUTTON_SIZE = 30;

/**
 * Height of a catalogue table row whose tallest cell holds `contentHeight` px of content.
 * The loading skeleton sizes its rows with this so the table does not grow when data lands.
 */
export const catalogueRowHeight = (contentHeight: number): number =>
  contentHeight + CATALOGUE_CELL_PADDING * 2;

/** Tint strength behind a status pill or a pricebook-type chip. */
export const CHIP_TINT_OPACITY = 0.1;

/**
 * Height shared by the pricebook-type chip and the status pill. They sit side by side
 * in the detail headers, where two different heights read as a misalignment.
 */
export const CATALOGUE_CHIP_HEIGHT = 32;

export const PRODUCT_LIST_PATH = "/product/products";
export const PRICE_BOOK_LIST_PATH = "/product/price-book";
export const PRODUCT_COST_PATH = "/product/cost";
/** ราคาตามช่วงเวลา: every book's price periods. */
export const PRICE_PERIOD_PATH = "/product/price-periods";
/** Query param of PRICE_PERIOD_PATH that opens the list on one period status. */
export const PRICE_PERIOD_STATUS_PARAM = "status";

export const pricePeriodListPath = (status: PricebookPeriodStatus) =>
  `${PRICE_PERIOD_PATH}?${PRICE_PERIOD_STATUS_PARAM}=${status}`;

export const productDetailsPath = (uuid: string) =>
  `${PRODUCT_LIST_PATH}/details/${uuid}`;

export const priceBookDetailsPath = (uuid: string) =>
  `${PRICE_BOOK_LIST_PATH}/details/${uuid}`;

/** Query param of the pricebook detail page that opens it on one tab. */
export const PRICE_BOOK_TAB_PARAM = "tab";
/** The detail page's ช่วงราคา tab. */
export const PRICE_BOOK_PERIODS_TAB = "periods";

/** The detail page's ตั้งเวลา tab (scheduled draft applies). */
export const PRICE_BOOK_SCHEDULES_TAB = "schedules";

export const priceBookPeriodsPath = (uuid: string) =>
  `${priceBookDetailsPath(uuid)}?${PRICE_BOOK_TAB_PARAM}=${PRICE_BOOK_PERIODS_TAB}`;

export const priceBookSchedulesPath = (uuid: string) =>
  `${priceBookDetailsPath(uuid)}?${PRICE_BOOK_TAB_PARAM}=${PRICE_BOOK_SCHEDULES_TAB}`;
